// ═══════════════════════════════════════════════════════════════
// V2 api-switch.ts — Compatibility shim for V1 import paths
//
// V1 pages import from '../../lib/api-switch'. In V2, this file
// re-exports the V2 equivalents with the same function signatures
// so V1 pages compile without modification.
// ═══════════════════════════════════════════════════════════════

export { TABLES } from '../api/tables';
export { FIELDS } from '../api/fields';

import { fetchRecords, createRecord as v2Create, updateRecord as v2Update, deleteRecord as v2Delete } from '../api/client';

// In dev, call the worker directly. In production (ogemc.pages.dev), route
// through the Pages Function proxy at /api/worker/* which sets the correct
// Origin header and avoids CORS failures.
const _isDev = import.meta.env.DEV;
export const WORKER_URL = _isDev
  ? (import.meta.env.VITE_WORKER_BASE || 'https://ogemc-worker.ogemc.workers.dev')
  : '/api/worker';
export const ORIGIN = 'https://apps.opengov.com';

// ── fetchTable → V2 fetchRecords (returns flat array) ───────────
export async function fetchTable<T = Record<string, unknown>>(
  tableKey: string,
  options?: { limit?: number; token?: string; anonymous?: boolean }
): Promise<T[]> {
  const limit = options?.limit ?? 1000;
  const result = await fetchRecords<T>(tableKey, { limit, anonymous: options?.anonymous });
  return result.records ?? (result as unknown as { data: T[] }).data ?? [];
}

// ── fetchRecordById — fetch a single record by numeric ID (anonymous-safe) ─
const _isDev2 = import.meta.env.DEV;
const _v2ProxyBase = _isDev2
  ? (import.meta.env.VITE_V2_API_BASE || 'https://gab-core-api.gab.ogintegration.us')
  : '/api/v2proxy';
const _v2AppId = import.meta.env.VITE_V2_APP_ID || '8q39q8q5n';

export async function fetchRecordById(
  tableKey: string,
  recordId: number | string,
): Promise<Record<string, unknown> | null> {
  try {
    const url = `${_v2ProxyBase}/v2/apps/${_v2AppId}/tables/${tableKey}/records/${recordId}`;
    const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
    return await res.json();
  } catch (e) {
    console.error(`fetchRecordById ${tableKey}/${recordId}:`, e);
    throw e;
  }
}

// ── createRecord — V1 takes Array<[key, value]>, V2 takes plain object ──
export async function createRecord(
  tableKey: string,
  fields: Array<[string, unknown]>,
  _token?: string | { anonymous?: boolean }
): Promise<number> {
  const data: Record<string, unknown> = {};
  for (const [k, v] of fields) data[k] = v;
  const anonymous = typeof _token === 'object' ? _token?.anonymous : false;
  const result = await v2Create<Record<string, unknown>>(tableKey, data, anonymous ? { anonymous: true } : undefined);
  return (result.record_id ?? result.id ?? result.Rid ?? 0) as number;
}

// ── updateRecord — V1 takes Array<[key, value]>, V2 takes plain object ──
export async function updateRecord(
  tableKey: string,
  rid: number,
  fields: Array<[string, unknown]>,
  _token?: string | { anonymous?: boolean }
): Promise<void> {
  const data: Record<string, unknown> = {};
  for (const [k, v] of fields) data[k] = v;
  const anonymous = typeof _token === 'object' ? _token?.anonymous : false;
  await v2Update(tableKey, rid, data, anonymous ? { anonymous: true } : undefined);
}

// ── deleteRecord ─────────────────────────────────────────────────
export async function deleteRecord(
  tableKey: string,
  rid: number,
  _token?: string
): Promise<void> {
  await v2Delete(tableKey, rid);
}

// ── callClaude — proxied through worker ──────────────────────────
// Accepts either:
//   callClaude("plain string prompt", "optional system prompt")
//   callClaude([{ role: 'user', content: '...' }], { system: '...', maxTokens: 1024 })
export async function callClaude(
  promptOrMessages: string | Array<{ role: string; content: string | unknown[] }>,
  systemOrOptions?: string | { system?: string; maxTokens?: number; stream?: boolean },
  stream?: boolean,
): Promise<string> {
  let messages: Array<{ role: string; content: string | unknown[] }>;
  let system: string | undefined;
  let maxTokens = 1024;

  if (typeof promptOrMessages === 'string') {
    messages = [{ role: 'user', content: promptOrMessages }];
    system = typeof systemOrOptions === 'string' ? systemOrOptions : undefined;
  } else {
    messages = promptOrMessages;
    if (systemOrOptions && typeof systemOrOptions === 'object') {
      system = systemOrOptions.system;
      maxTokens = systemOrOptions.maxTokens ?? 1024;
      stream = systemOrOptions.stream ?? stream;
    } else if (typeof systemOrOptions === 'string') {
      system = systemOrOptions;
    }
  }

  const resp = await fetch(`${WORKER_URL}/api/claude`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6',
      max_tokens: maxTokens,
      stream: stream ?? false,
      messages,
      system,
    }),
  });
  if (!resp.ok) throw new Error(`callClaude: ${resp.status}`);
  const data = await resp.json();
  return data?.content?.[0]?.text ?? data?.completion ?? '';
}

// ── User Management (via worker proxy) ───────────────────────────
export interface IgnatiusUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  /** Alias for name — may be returned by some API responses */
  fullName?: string;
  /** Phone number — may be returned by some API responses */
  phoneNumber?: string;
  /** Top-level role — may be returned by some API responses */
  topLevelRole?: string;
  role?: string;
  isAdmin?: boolean;
  active?: boolean;
}

export async function fetchIgnatiusUsers(): Promise<IgnatiusUser[]> {
  const resp = await fetch(`${WORKER_URL}/api/users`, {
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
  });
  if (!resp.ok) throw new Error(`fetchIgnatiusUsers: ${resp.status}`);
  const users: IgnatiusUser[] = await resp.json();
  // Normalize: ensure fullName, email are always strings
  return users.map(u => ({
    ...u,
    email: u.email || '',
    name: u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || '',
    fullName: u.fullName || u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || '',
  }));
}

export async function validateEmail(email: string): Promise<unknown> {
  const resp = await fetch(`${WORKER_URL}/api/users/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
    body: JSON.stringify({ email }),
  });
  if (!resp.ok) throw new Error(`validateEmail: ${resp.status}`);
  return resp.json();
}

export async function createIgnatiusUser(data: {
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
}): Promise<{ success: boolean; userId?: string }> {
  const resp = await fetch(`${WORKER_URL}/api/users/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
    body: JSON.stringify(data),
  });
  if (!resp.ok) throw new Error(`createIgnatiusUser: ${resp.status}`);
  return resp.json();
}

export async function resetUserPassword(
  email: string,
  newPassword: string,
  _adminToken: string,
): Promise<{ success: boolean }> {
  const resp = await fetch(`${WORKER_URL}/api/users/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Origin: ORIGIN },
    body: JSON.stringify({ email, newPassword }),
  });
  if (!resp.ok) throw new Error(`resetUserPassword: ${resp.status}`);
  return resp.json();
}

// ── loginUser ────────────────────────────────────────────────────
import { login } from '../api/client';
export { login as loginUser };

// ── No-op record event listener (workflow engine handles this differently in V2) ──
type RecordEventListener = (
  tableKey: string,
  event: 'create' | 'update' | 'delete',
  rid: number,
  data: Record<string, unknown>,
  previousData?: Record<string, unknown>,
) => void;

let _recordEventListener: RecordEventListener | null = null;
export function registerRecordEventListener(fn: RecordEventListener) {
  _recordEventListener = fn;
}
